import { createDictionary, createDictionaryMultiple } from "@tedivo/tedivo-pure-helpers";
import { allBaysWith40sArePairedValidator } from "./baysData/allBaysWith40sArePairedValidator";
import { bay40sLocationValidator } from "./baysData/bay40sLocationValidator";
import { bayLevelStructureValidator } from "./structure/bayLevelStructureValidator";
import { centerLineRowValidator } from "./baysData/centerLineRowValidator";
import { cgsValidator } from "./CGs/cgsValidator";
import { lidDataForBaysValidator } from "./lids/lidDataForBaysValidator";
import { lidStructureValidator } from "./structure/lidStructureValidator";
import { rootStructureValidator } from "./structure/rootStructureValidator";
import { rowWeightsValidator } from "./baysData/rowWeightsValidator";
import { slotStructureValidator } from "./structure/slotStructureValidator";
import { vesselPartsStructureValidator } from "./structure/vesselPartsStructureValidator";
export const VALIDATOR_STAGES = ["structure", "data", "semantic"];
/** The translation keys of the outputs the engine itself produces */
export const SKIPPED_STRUCTURE_KEY = "validators:common.skippedStructure";
export const CRASHED_KEY = "validators:common.crashed";
export function stageOf(validator) {
    var _a;
    return (_a = validator.stage) !== null && _a !== void 0 ? _a : "semantic";
}
export class OvdValidator {
    constructor() {
        this.lastResult = [];
        this.lastResultByName = undefined;
        /** Get the last result of the validators by scope */
        this.getLastResult = (scopes) => {
            return this.lastResult.filter((a) => !scopes || (a.specialScopes && a.specialScopes.some((sc) => scopes.includes(sc))));
        };
        /** The last results of a stage */
        this.getLastResultByStage = (stage) => this.lastResult.filter((a) => a.stage === stage);
        /** The last results that failed with exactly this severity */
        this.getLastResultBySeverity = (severity) => this.lastResult.filter((a) => a.severity === severity);
        /** Gets a single result by name */
        this.getLastResultByName = (name) => {
            if (!this.lastResultByName) {
                this.lastResultByName = createDictionary(this.lastResult, (a) => a.name);
            }
            return this.lastResultByName[name];
        };
        const valsById = createDictionaryMultiple(OvdValidator.validators, (a) => a.name);
        /** Check there aren't duplicate validators by name */
        Object.keys(valsById).forEach((key) => {
            const vals = valsById[key];
            if (vals.length > 1) {
                throw new Error(`Duplicate validator name: ${key}`);
            }
        });
    }
    /**
     * Runs the structure validators only, on the raw JSON. Use it to decide whether the
     * file can be handed to the CG pipeline (`BayLcgVcgTcgAndPairings`) at all.
     */
    static validateStructure(json) {
        return OvdValidator.run(json, undefined, undefined, ["structure"]);
    }
    /** Execute all the validators and cache the result */
    validate(json, lcgVcgTcgAndPairings, lcgsBy20Bay, options) {
        var _a;
        const result = OvdValidator.run(json, lcgVcgTcgAndPairings, lcgsBy20Bay, (_a = options === null || options === void 0 ? void 0 : options.stages) !== null && _a !== void 0 ? _a : VALIDATOR_STAGES);
        this.lastResult = result;
        this.lastResultByName = undefined;
        return result;
    }
    static run(json, lcgVcgTcgAndPairings, lcgsBy20Bay, stages) {
        const input = { data: { json, lcgVcgTcgAndPairings, lcgsBy20Bay } };
        const results = [];
        let structureOk = true;
        VALIDATOR_STAGES.forEach((stage) => {
            if (!stages.includes(stage))
                return;
            OvdValidator.validators
                .filter((validator) => stageOf(validator) === stage)
                .forEach((validator) => {
                if (stage !== "structure" && !structureOk) {
                    results.push(notRun(validator, SKIPPED_STRUCTURE_KEY, "info"));
                    return;
                }
                results.push(runSafely(validator, input));
            });
            if (stage === "structure") {
                structureOk = !results.some((r) => r.stage === "structure" && r.severity === "error");
            }
        });
        return results;
    }
}
/** Registration order is kept within each stage. Stages run structure → data → semantic */
OvdValidator.validators = [
    rootStructureValidator,
    bayLevelStructureValidator,
    slotStructureValidator,
    lidStructureValidator,
    vesselPartsStructureValidator,
    allBaysWith40sArePairedValidator,
    rowWeightsValidator,
    bay40sLocationValidator,
    centerLineRowValidator,
    lidDataForBaysValidator,
    cgsValidator,
];
function runSafely(validator, input) {
    var _a;
    try {
        const out = validator.fn(input);
        // Validators written before stages existed don't tag their output
        if (out.stage === undefined)
            out.stage = stageOf(validator);
        if (out.severity === undefined && !out.isValid)
            out.severity = (_a = validator.severity) !== null && _a !== void 0 ? _a : "error";
        return out;
    }
    catch (_b) {
        return notRun(validator, CRASHED_KEY, "error");
    }
}
function notRun(validator, messageTranslationKey, severity) {
    return {
        name: validator.name,
        isValid: false,
        messageTranslationKey,
        specialScopes: validator.specialScopes,
        severity,
        stage: stageOf(validator),
        skipped: true,
        result: undefined,
    };
}
